// /pages/WipReportByLotPage.js

import React, { useState, useEffect,  useRef } from "react";
import axios from "axios";
import DataGrid, {  Column, FilterRow, HeaderFilter, SearchPanel, Sorting, Export, Paging } from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import SelectBox from "devextreme-react/select-box";
import notify from 'devextreme/ui/notify';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

const WIP_REPORT_API_URL = `${API_URL}/api/oracle/wip-report-by-lot`;
const CUST_API_URL = `${API_URL}/api/local/common/custs`;
const DEVICE_API_URL = `${API_URL}/api/local/common/devices`;

// Factory별 공정 순서를 객체로 정의합니다.
const OPER_ORDER_BY_FACTORY = {
  'FT01': [
    'TB', 'IQC', 'DEREEL', 'BKE1', 'FTST', 'FTHT', 'FTCT', 'FTST2',
    'FSLT', 'FSLT2', 'QEI', 'LIS', 'BKE', 'QVI', 'TNR', 'RQC',
    'IPK', 'QPI', 'FGS', 'SHIP'
  ],
  'PT01': [
    'TB', 'IQC', 'PTST', 'BKE', 'PTST2', 'INK', 'BKE2', 'PTST3',
    'AVI', 'PTST4', 'QEI', 'RQC', 'AC', 'RQC2', 'WPK', 'QPI',
    'FGS', 'SHIP'
  ],
};

const processPivotData = (rawData, factory) => {
  if (!rawData || rawData.length === 0) {
    return { pivotedData: [], dynamicColumns: [] };
  }
  const definedOrder = OPER_ORDER_BY_FACTORY[factory] || [];
  const groupedData = new Map();
  const operSet = new Set();

  rawData.forEach(row => {
    const key = row.LOT_ID;
    if (!groupedData.has(key)) {
      groupedData.set(key, { ...row, Opers: {} });
    }
    const entry = groupedData.get(key);
    if (row.OPER && row.NOW_QTY !== null) {
      entry.Opers[row.OPER] = row.NOW_QTY;
      operSet.add(row.OPER);
    }
  });

  const dynamicColumns = Array.from(operSet).sort((a, b) => {
    const indexA = definedOrder.indexOf(a);
    const indexB = definedOrder.indexOf(b);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a.localeCompare(b);
  });

  const pivotedData = Array.from(groupedData.values()).map(item => {
    const finalRow = { ...item };
    dynamicColumns.forEach(operName => {
      finalRow[operName] = item.Opers[operName] || null;
    });
    delete finalRow.Opers;
    delete finalRow.OPER;
    return finalRow;
  });

  return { pivotedData, dynamicColumns };
};

export default function WipReportByLotPage({ user }) {
  const [data, setData] = useState([]);
  const [dynamicOperColumns, setDynamicOperColumns] = useState([]);
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  
  const [selectedFactory, setSelectedFactory] = useState('FT01');
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState('ALL');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const dataGridRef = useRef(null);

  useEffect(() => {
    if (!selectedFactory) return;
    axios.get(CUST_API_URL, { params: { factory: selectedFactory } })
      .then(res => setCustList(res.data.data || []))
      .catch(err => console.error("CUST 목록 조회 실패:", err));
  }, [selectedFactory]);

  useEffect(() => {
    if (!selectedFactory || !selectedCust) {
      setDeviceList([]);
      setSelectedDevice('ALL');
      return;
    }
    axios.get(DEVICE_API_URL, { params: { factory: selectedFactory, custCd: selectedCust } })
      .then(res => {
        const fetchedDevices = res.data.data || [];
        const allOption = { CODE_CD: 'ALL' };
        setDeviceList([allOption, ...fetchedDevices]);
        setSelectedDevice('ALL');
      })
      .catch(err => console.error("DEVICE 목록 조회 실패:", err));
  }, [selectedFactory, selectedCust]);

  useEffect(() => {
    if (user?.role === "CUST" && custList.length > 0) {
      const found = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      if (found) setSelectedCust(found.CODE_CD);
    }
  }, [user, custList]);
  
  // ★★★ [수정] useCallback의 의존성 배열을 제거하여 항상 최신 state를 참조하도록 변경 ★★★
  const fetchData = () => {
    if (!selectedFactory || !selectedCust) {
      notify("Factory와 CUST는 필수 선택 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    setData([]);
    setDynamicOperColumns([]);

    const params = {
      factory: selectedFactory,
      custCd: selectedCust,
      device: selectedDevice,
    };
    
    axios.get(WIP_REPORT_API_URL, { params })
      .then(res => {
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        const { pivotedData, dynamicColumns } = processPivotData(res.data.data, selectedFactory);
        setData(pivotedData.map((row, idx) => ({ ...row, id: idx })));
        setDynamicOperColumns(dynamicColumns);
      })
      .catch(e => {
        const errorMessage = e.response?.data?.retMsg || e.message;
        setError(`조회 오류: ${errorMessage}`);
        notify(errorMessage, 'error', 3000);
        setData([]);
      })
      .finally(() => setLoading(false));
  };
  // Excel 내보내기 함수
  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("WIP_Report_by_Lot");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true })
      .then(() => workbook.xlsx.writeBuffer())
      .then(buffer => saveAs(new Blob([buffer], { type: "application/octet-stream" }), "WIP_Report_by_Lot.xlsx"));
    e.cancel = true;
  };
  
  return (
    <div style={{ padding: 20, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>WIP Report by Lot</h3>
        <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center' }}>
          <SelectBox 
            items={['FT01', 'PT01']}
            value={selectedFactory} 
            onValueChanged={e => setSelectedFactory(e.value)}
            label="FACTORY" 
            width={150} 
          />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_NM" onValueChanged={e => setSelectedCust(e.value)} disabled={user?.role === "CUST"} searchEnabled showClearButton={user?.role !== "CUST"} placeholder="Select CUST" label="CUST" width={220} />
          <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} searchEnabled label="DEVICE" width={220} />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold', marginTop: '10px' }}>{error}</p>}
      </div>
      
      {/* 2. 그리드 영역 */}
      <div style={{ flex: 1, position: 'relative' }}>
        {loading && <div className="loading-overlay"></div>}
        <DataGrid
          ref={dataGridRef}
          dataSource={data}
          keyExpr="id"
          onExporting={onExporting}
          height="100%"
          showBorders={true}
          allowColumnResizing={true}
          columnAutoWidth={true}
          hoverStateEnabled={true}
        >
          <Export enabled={true} />
          <Paging defaultPageSize={20} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} width={240} placeholder="Search..." />
          <Sorting mode="multiple" />       
    
          {/* ★★★ [수정] 컬럼 정의 (빠진 컬럼 추가 및 순서 조정) ★★★ */}
          <Column dataField="FACTORY" caption="Factory" width={100} fixed={true} />
          <Column dataField="CUSTOMER" caption="Customer" width={120} fixed={true} />
          <Column dataField="MAT_ID" caption="Device" width={150} fixed={true} />
          <Column dataField="LOT_ID" caption="Lot ID" width={150} />
          <Column dataField="WW" caption="W.W" width={80} />
          <Column dataField="CT" caption="C/T" dataType="number" width={80} />
          <Column dataField="LOT_TYPE" caption="Lot Type" width={100} />
          <Column dataField="HOLD_FLAG" caption="Hold" width={80} alignment="center" />
          <Column dataField="HOLD_CODE" caption="Hold Code" width={120} />
          <Column dataField="IN_QTY" caption="In Qty" dataType="number" alignment="right" width={100} />
          <Column dataField="TOTAL_QTY" caption="Total Qty" dataType="number" alignment="right" width={100} />
          <Column dataField="NOW_QTY" caption="Now Qty" dataType="number" alignment="right" width={100} />
          <Column dataField="NOW_DIE_QTY" caption="Now Die Qty" dataType="number" alignment="right" width={120} />
          
          {/* 동적 OPER 컬럼 렌더링 */}
           {dynamicOperColumns.map(operName => (
            <Column
              key={operName}
              dataField={operName}
              caption={operName}
              dataType="number"
              alignment="right"
              width={90}
            />
          ))}

          <Column dataField="PGM1" caption="PGM1" width={150} />
          <Column dataField="PGM2" caption="PGM2" width={150} />
          <Column dataField="PGM3" caption="PGM3" width={150} />
          <Column dataField="HOLD_CODE1" caption="Hold Code 1" width={150} />
          <Column dataField="HOLD_COMMENT1" caption="Hold Comment" width={250} />
          
        
        </DataGrid>
      </div>
    </div>
  );
}